import useSWR from "swr";
import getConfig from "../configuration";
import type { LoginCredentials, AuthResponse } from "../types/auth";
import {
  apiDebugLog,
  getIsRedirecting,
  resetRedirectingFlag,
  markCompanySelected,
  markSubscriptionSelected,
  isInGracePeriod,
  handleAuthErrorResponse,
} from "./auth-session";

export {
  getIsRedirecting,
  resetRedirectingFlag,
  markCompanySelected,
  markSubscriptionSelected,
  isInGracePeriod,
};

const { apiUrl } = getConfig();

type Json = unknown;
function sanitizeString(value: string): string {
  return value
    .trim()
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      const isDisallowedControl =
        (code >= 0 && code <= 8) ||
        code === 11 ||
        code === 12 ||
        (code >= 14 && code <= 31) ||
        code === 127;
      return !isDisallowedControl;
    })
    .join("");
}

function sanitizePayload<T>(value: T): T {
  if (typeof value === "string") {
    return sanitizeString(value) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizePayload(item)) as T;
  }

  if (value && typeof value === "object") {
    const sanitizedEntries = Object.entries(
      value as Record<string, unknown>,
    ).map(([key, nestedValue]) => [key, sanitizePayload(nestedValue)]);
    return Object.fromEntries(sanitizedEntries) as T;
  }

  return value;
}

export class ApiError extends Error {
  status: number;
  details?: unknown;
  reasonCode?: string;
  constructor(
    message: string,
    status: number,
    details?: unknown,
    reasonCode?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
    this.reasonCode = reasonCode;
  }
}

function getAuthToken(): string | null {
  try {
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      apiDebugLog("localStorage no disponible", { action: "getAuthToken" });
      return null;
    }

    const token = localStorage.getItem("token");
    if (token === "" || (token && token.trim() === "")) {
      apiDebugLog("Token vacío en localStorage", { action: "getAuthToken" });
      return null;
    }

    return token;
  } catch (error) {
    apiDebugLog("Error leyendo token", {
      action: "getAuthTokenError",
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

function resolveUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return pathOrUrl;
  }
  return `${apiUrl}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function extractApiErrorMessage(
  payload: unknown,
  statusText: string,
): { message: string; reasonCode?: string } {
  if (payload && typeof payload === "object") {
    const asRecord = payload as Record<string, unknown>;
    const reasonCode =
      typeof asRecord.reasonCode === "string" ? asRecord.reasonCode : undefined;
    const rawMessage = asRecord.message;
    if (typeof rawMessage === "string" && rawMessage.trim() !== "") {
      return { message: rawMessage, reasonCode };
    }
    if (Array.isArray(rawMessage)) {
      const messages = rawMessage
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      if (messages.length > 0) {
        return { message: messages.join(". "), reasonCode };
      }
    }
    if (rawMessage && typeof rawMessage === "object") {
      const nested = rawMessage as Record<string, unknown>;
      if (typeof nested.message === "string" && nested.message.trim() !== "") {
        const nestedReason =
          typeof nested.reasonCode === "string"
            ? nested.reasonCode
            : reasonCode;
        return { message: nested.message, reasonCode: nestedReason };
      }
    }
    if (typeof asRecord.error === "string" && asRecord.error.trim() !== "") {
      return { message: asRecord.error, reasonCode };
    }
  }
  return { message: statusText || "Request failed" };
}

function emitPermissionDeniedHint(
  pathOrUrl: string,
  payload: unknown,
  reasonCode?: string,
): void {
  if (typeof window === "undefined") return;
  if (reasonCode !== "MISSING_PERMISSION" && reasonCode !== "MISSING_CONTEXT") {
    return;
  }
  if (!payload || typeof payload !== "object") return;

  const data = payload as Record<string, unknown>;
  const requiredPermissionsRaw =
    reasonCode === "MISSING_CONTEXT"
      ? data.requiredContext
      : data.requiredPermissions;
  const missingPermissionsRaw =
    reasonCode === "MISSING_CONTEXT"
      ? data.missingContext
      : data.missingPermissions;
  const requiredPermissions = Array.isArray(requiredPermissionsRaw)
    ? requiredPermissionsRaw.filter(
        (item): item is string => typeof item === "string",
      )
    : [];
  const missingPermissions = Array.isArray(missingPermissionsRaw)
    ? missingPermissionsRaw.filter(
        (item): item is string => typeof item === "string",
      )
    : requiredPermissions;

  if (requiredPermissions.length === 0) return;

  const actionLabel =
    reasonCode === "MISSING_CONTEXT"
      ? "Solicitud sin contexto requerido"
      : "Ejecutar acción protegida";

  window.dispatchEvent(
    new CustomEvent("permission-denied-hint", {
      detail: {
        scope: "action",
        resource: pathOrUrl,
        actionLabel,
        requiredPermissions,
        missingPermissions,
        timestamp: new Date().toISOString(),
      },
    }),
  );
}

function buildRequestHeaders(
  init:
    | (RequestInit & {
        json?: Json;
        companyId?: string | null;
        subscriptionId?: string | null;
      })
    | undefined,
  token: string | null,
  companyId: string | null,
  overrideSubscriptionId?: string | null,
): Headers {
  const headers = new Headers(init?.headers || {});

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  if (init?.json && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (companyId && companyId !== "SYSTEM" && !headers.has("x-company-id")) {
    headers.set("x-company-id", companyId);
  }

  const subscriptionId =
    overrideSubscriptionId ??
    (typeof window !== "undefined"
      ? localStorage.getItem("selectedSubscriptionId")
      : null);
  if (subscriptionId && !headers.has("x-subscription-id")) {
    headers.set("x-subscription-id", subscriptionId);
  }

  return headers;
}

function handleAuthError(
  status: number,
  pathOrUrl: string,
  payload?: unknown,
): void {
  handleAuthErrorResponse({
    status,
    pathOrUrl,
    payload,
    hasToken: !!getAuthToken(),
  });
}

async function apiFetch<T = unknown>(
  pathOrUrl: string,
  init?: RequestInit & {
    json?: Json;
    companyId?: string | null;
    subscriptionId?: string | null;
  },
): Promise<T> {
  const sanitizedJson =
    init?.json !== undefined ? sanitizePayload(init.json) : undefined;
  const token = getAuthToken();
  let companyId =
    init?.companyId ??
    (typeof window !== "undefined"
      ? localStorage.getItem("selectedCompanyId")
      : null);

  // SOLUCIÓN 1: Si estamos en período de gracia y no hay companyId, esperar un momento
  // Esto previene errores 401/403 causados por peticiones que se hacen antes de que
  // el companyId esté completamente sincronizado después de seleccionar sociedad
  if (isInGracePeriod() && !companyId && token) {
    const maxWait = 300;
    const checkInterval = 50;
    let waited = 0;

    while (waited < maxWait && !companyId) {
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
      companyId =
        localStorage.getItem("selectedCompanyId") ?? init?.companyId ?? null;
      waited += checkInterval;
    }

    apiDebugLog("grace wait for companyId", {
      pathOrUrl,
      companyId,
      waited,
    });
  }

  apiDebugLog("apiFetch", {
    pathOrUrl,
    hasToken: !!token,
    companyId,
    method: init?.method || "GET",
    inGracePeriod: isInGracePeriod(),
  });

  const headers = buildRequestHeaders(
    init,
    token,
    companyId,
    init?.subscriptionId,
  );

  const fetchUrl = resolveUrl(pathOrUrl);
  const body: BodyInit | null | undefined =
    sanitizedJson !== undefined ? JSON.stringify(sanitizedJson) : init?.body;

  let response: Response;
  try {
    response = await fetch(fetchUrl, {
      credentials: "include",
      ...init,
      headers,
      body,
    });
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    console.warn(detail);
    const looksLikeNetwork =
      err instanceof TypeError ||
      /failed to fetch|network|load failed|aborted/i.test(detail);
    const message = looksLikeNetwork
      ? `No se pudo conectar con el servidor (${detail}). Comprueba que la API esté en ejecución, que VITE_API_URL sea correcta y que CORS permita el origen del front.`
      : `Error de red: ${detail}`;
    throw new ApiError(message, 0, err);
  }

  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const payload = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => "");

  if (
    !response.ok &&
    (response.status === 401 || response.status === 403) &&
    isInGracePeriod()
  ) {
    apiDebugLog("grace period retry", {
      pathOrUrl,
      status: response.status,
      hasToken: !!token,
      hasCompanyId: !!companyId,
    });

    await new Promise((resolve) => setTimeout(resolve, 200));

    const retryToken = getAuthToken();
    const retryCompanyId =
      init?.companyId ??
      (typeof window !== "undefined"
        ? localStorage.getItem("selectedCompanyId")
        : null);
    const retrySubscriptionId =
      init?.subscriptionId ??
      (typeof window !== "undefined"
        ? localStorage.getItem("selectedSubscriptionId")
        : null);

    if (retryToken && (retryCompanyId || retrySubscriptionId)) {
      const retryHeaders = buildRequestHeaders(
        init,
        retryToken,
        retryCompanyId,
        retrySubscriptionId,
      );

      try {
        const retryResponse = await fetch(resolveUrl(pathOrUrl), {
          credentials: "include",
          ...init,
          headers: retryHeaders,
          body:
            sanitizedJson !== undefined
              ? JSON.stringify(sanitizedJson)
              : init?.body,
        });

        if (retryResponse.ok) {
          const retryContentType =
            retryResponse.headers.get("content-type") || "";
          const retryIsJson = retryContentType.includes("application/json");
          const retryPayload = retryIsJson
            ? await retryResponse.json().catch(() => null)
            : await retryResponse.text().catch(() => "");

          apiDebugLog("grace period retry success", {
            pathOrUrl,
            status: retryResponse.status,
          });

          return retryPayload as T;
        }
      } catch (retryError) {
        apiDebugLog("grace period retry error", {
          pathOrUrl,
          error:
            retryError instanceof Error
              ? retryError.message
              : String(retryError),
        });
      }
    }
  }

  if (!response.ok) {
    const { message, reasonCode } = extractApiErrorMessage(
      payload,
      response.statusText,
    );

    apiDebugLog("request failed", {
      pathOrUrl,
      status: response.status,
      message: String(message),
      reasonCode,
      hasToken: !!token,
      inGracePeriod: isInGracePeriod(),
    });

    handleAuthError(response.status, pathOrUrl, payload);
    if (response.status === 403) {
      emitPermissionDeniedHint(pathOrUrl, payload, reasonCode);
    }

    throw new ApiError(String(message), response.status, payload, reasonCode);
  }

  apiDebugLog("request success", {
    pathOrUrl,
    status: response.status,
    method: init?.method || "GET",
  });

  return payload as T;
}

// SWR fetcher global (clave string → GET JSON)
export const fetcher = <T = unknown>(key: string): Promise<T> =>
  apiFetch<T>(key);

// Helper básico de SWR
export const useApi = <T>(url: string) => useSWR<T>(url, fetcher);

// Auth API minimal
export const authApi = {
  login(credentials: LoginCredentials): Promise<AuthResponse> {
    return apiFetch<AuthResponse>("/auth/login", {
      method: "POST",
      json: credentials,
    });
  },
  validate(): Promise<{ valid: boolean }> {
    return apiFetch("/auth/validate");
  },
  refresh(): Promise<{ token: string }> {
    return apiFetch("/auth/refresh", { method: "POST" });
  },
  profile(): Promise<{
    id: string;
    email: string;
    name: string;
    lastName: string;
  }> {
    return apiFetch("/auth/profile");
  },
  accessSummary(): Promise<{
    positions: Array<{
      id: string;
      code: string;
      name: string;
      isPrimary: boolean;
      companyId: string;
      companyName: string;
    }>;
    roles: Array<{
      id: string;
      name: string;
    }>;
    permissions: Array<{
      id: string;
      code: string;
      description: string;
    }>;
  }> {
    return apiFetch("/auth/access-summary");
  },
  companies(): Promise<
    Array<{ id: string; name: string; subscriptionId: string }>
  > {
    return apiFetch("/auth/companies");
  },
  subscriptions(): Promise<
    Array<{
      id: string;
      subscriptionId: string;
      name: string;
      planName: string;
      description?: string;
      planId?: string;
      status: string;
      active: boolean;
      isSystem?: boolean;
    }>
  > {
    return apiFetch("/auth/subscriptions");
  },
  forgotPassword(email: string): Promise<{ message: string }> {
    return apiFetch("/auth/forgot-password", {
      method: "POST",
      json: { email },
    });
  },
  resetPassword(
    token: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    return apiFetch("/auth/reset-password", {
      method: "POST",
      json: { token, newPassword },
    });
  },
  setInitialPassword(
    token: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    return apiFetch("/auth/set-initial-password", {
      method: "POST",
      json: { token, newPassword },
    });
  },
  unlockAccount(userId: string): Promise<{ message: string }> {
    return apiFetch("/auth/unlock-account", {
      method: "POST",
      json: { userId },
    });
  },
};

// User profile API (my-info, my-password - no company context required)
export const userProfileApi = {
  getMyInfo(): Promise<{
    id: string;
    name: string;
    lastName: string;
    email: string;
    rut?: string;
    phone?: string;
    nationality?: { id: string; name: string; alpha2: string; alpha3: string };
    role?: { id: string; name: string; isSystem?: boolean };
  }> {
    return apiFetch("/user/my-info");
  },
  updateMyInfo(data: {
    name?: string;
    lastName?: string;
    rut?: string;
    phone?: string;
    nationalityId?: string;
  }): Promise<{
    id: string;
    name: string;
    lastName: string;
    email: string;
    rut?: string;
    phone?: string;
    nationality?: { id: string; name: string; alpha2: string; alpha3: string };
  }> {
    return apiFetch("/user/my-info", {
      method: "PUT",
      json: data,
    });
  },
  updateMyPassword(data: {
    password: string;
    passwordConfirmation: string;
  }): Promise<{ message: string }> {
    return apiFetch("/user/my-password", {
      method: "PUT",
      json: data,
    });
  },
};

export interface RoleQueryDto {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search?: string;
  filters?: Array<{
    columnKey: string;
    operator: string;
    value: string | string[];
  }>;
  source?: "core" | "tenant";
  isTenant?: boolean;
  companyId?: string;
  active?: boolean;
}

interface RoleRow {
  id: string;
  name: string;
  description: string;
  active: boolean;
  isTenant?: boolean;
  isSystem?: boolean;
  type?: "core" | "tenant";
  createdAt?: string;
  updatedAt?: string;
  createdById?: string;
  updatedById?: string;
  createdByName?: string;
  updatedByName?: string;
  company?: { id: string; name: string };
}

interface RoleResponse {
  id: string;
  name: string;
  description: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  isTenant?: boolean;
  isSystem?: boolean;
}

// Role API functions
export const roleApi = {
  getAll(query?: RoleQueryDto): Promise<{
    data: RoleRow[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (query?.page) params.append("page", query.page.toString());
    if (query?.limit) params.append("limit", query.limit.toString());
    if (query?.sortBy) params.append("sortBy", query.sortBy);
    if (query?.sortOrder) params.append("sortOrder", query.sortOrder);
    if (query?.search) params.append("search", query.search);
    if (query?.source) params.append("source", query.source);
    if (query?.isTenant !== undefined)
      params.append("isTenant", String(query.isTenant));
    if (query?.companyId) params.append("companyId", query.companyId);
    if (query?.active !== undefined)
      params.append("active", String(query.active));
    if (query?.filters && query.filters.length > 0) {
      params.append("filters", JSON.stringify(query.filters));
    }
    const qs = params.toString();
    return apiFetch(`/role${qs ? `?${qs}` : ""}`);
  },

  getBySubscription(
    subscriptionId: string,
    query?: RoleQueryDto,
  ): Promise<{
    data: RoleRow[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (query?.page) params.append("page", query.page.toString());
    if (query?.limit) params.append("limit", query.limit.toString());
    if (query?.sortBy) params.append("sortBy", query.sortBy);
    if (query?.sortOrder) params.append("sortOrder", query.sortOrder);
    if (query?.search) params.append("search", query.search);
    if (query?.source) params.append("source", query.source);
    if (query?.isTenant !== undefined)
      params.append("isTenant", String(query.isTenant));
    if (query?.companyId) params.append("companyId", query.companyId);
    if (query?.active !== undefined)
      params.append("active", String(query.active));
    if (query?.filters && query.filters.length > 0) {
      params.append("filters", JSON.stringify(query.filters));
    }
    const qs = params.toString();
    return apiFetch(`/role${qs ? `?${qs}` : ""}`, { subscriptionId });
  },

  create(
    data: {
      name: string;
      description: string;
      active?: boolean;
      isTenant?: boolean;
      isSystem?: boolean;
    },
    options?: { subscriptionId?: string },
  ): Promise<RoleResponse> {
    return apiFetch("/role", {
      method: "POST",
      json: data,
      subscriptionId: options?.subscriptionId,
    });
  },

  getById(
    id: string,
    options?: { source?: string; subscriptionId?: string },
  ): Promise<
    RoleResponse & {
      type?: "core" | "tenant";
      company?: { id: string; name: string };
    }
  > {
    const qs = options?.source ? `?source=${options.source}` : "";
    return apiFetch(`/role/${id}${qs}`, {
      subscriptionId: options?.subscriptionId,
    });
  },

  update(
    id: string,
    data: {
      name?: string;
      description?: string;
      active?: boolean;
      isSystem?: boolean;
      isTenant?: boolean;
    },
    options?: { source?: string; subscriptionId?: string },
  ): Promise<RoleResponse> {
    const qs = options?.source ? `?source=${options.source}` : "";
    return apiFetch(`/role/${id}${qs}`, {
      method: "PATCH",
      json: data,
      subscriptionId: options?.subscriptionId,
    });
  },

  delete(
    id: string,
    options?: { source?: string; subscriptionId?: string },
  ): Promise<void> {
    const qs = options?.source ? `?source=${options.source}` : "";
    return apiFetch(`/role/${id}${qs}`, {
      method: "DELETE",
      subscriptionId: options?.subscriptionId,
    });
  },
};

// Permission API functions
export const permissionApi = {
  // Obtener todos los permisos
  getAll(query?: {
    source?: "core" | "tenant";
    subscriptionId?: string;
  }): Promise<{
    data: Array<{
      id: string;
      code: string;
      description: string;
      active: boolean;
      companyId?: string;
      type?: "core" | "tenant";
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (query?.source) params.append("source", query.source);
    const qs = params.toString();
    return apiFetch(`/permission${qs ? `?${qs}` : ""}`, {
      subscriptionId: query?.subscriptionId,
    });
  },

  // Obtener permisos del usuario actual
  getUserPermissions(companyId?: string | null): Promise<
    Array<{
      id: string;
      code: string;
      description: string;
      active: boolean;
    }>
  > {
    return apiFetch("/auth/permissions", {
      companyId: companyId ?? null,
    });
  },
};

// Role-Permission API functions
export const rolePermissionApi = {
  // Crear asociación rol-permiso
  create(
    data: {
      roleId: string;
      permissionId: string;
    },
    options?: { source?: string; subscriptionId?: string },
  ): Promise<{ id: string; roleId: string; permissionId: string }> {
    const qs = options?.source ? `?source=${options.source}` : "";
    return apiFetch(`/role-permission${qs}`, {
      method: "POST",
      json: data,
      subscriptionId: options?.subscriptionId,
    });
  },

  // Obtener permisos de un rol
  getByRole(
    roleId: string,
    options?: { source?: string; subscriptionId?: string },
  ): Promise<
    Array<{
      id: string;
      roleId: string;
      permissionId: string;
      permission: {
        id: string;
        name: string;
        module: string;
        submodule: string | null;
        action: string;
      };
    }>
  > {
    const qs = options?.source ? `?source=${options.source}` : "";
    return apiFetch(`/role-permission/role/${roleId}${qs}`, {
      subscriptionId: options?.subscriptionId,
    });
  },

  // Eliminar asociación rol-permiso (baja lógica)
  delete(
    id: string,
    options?: { source?: string; subscriptionId?: string },
  ): Promise<void> {
    const qs = options?.source ? `?source=${options.source}` : "";
    return apiFetch(`/role-permission/${id}${qs}`, {
      method: "PATCH",
      json: { active: false },
      subscriptionId: options?.subscriptionId,
    });
  },

  // Obtener todas las asociaciones con filtros
  getAll(query?: {
    page?: number;
    limit?: number;
    roleId?: string;
    permissionId?: string;
    active?: boolean;
    source?: string;
    subscriptionId?: string;
  }): Promise<{
    data: Array<{
      id: string;
      roleId: string;
      permissionId: string;
      role: { id: string; name: string };
      permission: {
        id: string;
        name: string;
        module: string;
        submodule: string | null;
        action: string;
      };
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (query?.page) searchParams.set("page", query.page.toString());
    if (query?.limit) searchParams.set("limit", query.limit.toString());
    if (query?.roleId) searchParams.set("roleId", query.roleId);
    if (query?.permissionId)
      searchParams.set("permissionId", query.permissionId);
    if (query?.active !== undefined)
      searchParams.set("active", String(query.active));
    if (query?.source) searchParams.set("source", query.source);

    const queryString = searchParams.toString();
    return apiFetch(`/role-permission${queryString ? `?${queryString}` : ""}`, {
      subscriptionId: query?.subscriptionId,
    });
  },
};

// API de nacionalidades
export const nationalityApi = {
  getAll(): Promise<
    Array<{
      id: string;
      name: string;
      alpha2: string;
      alpha3: string;
    }>
  > {
    return apiFetch<{
      data: Array<{
        id: string;
        name: string;
        alpha2: string;
        alpha3: string;
      }>;
    }>("/nationality").then((res) => res.data);
  },
};

// API de usuarios
export const userApi = {
  // Obtener todos los usuarios
  getAll(): Promise<{
    data: Array<{
      id: string;
      name: string;
      lastName: string;
      email: string;
      active: boolean;
      lastLoginAt?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    return apiFetch("/user");
  },

  // Crear un nuevo usuario
  create(data: {
    name: string;
    lastName?: string;
    email: string;
    password: string;
    currentCompanyId?: string;
    rut?: string;
    phone?: string;
    nationalityId?: string;
  }): Promise<{
    id: string;
    name: string;
    lastName: string;
    email: string;
    active: boolean;
    lastLoginAt?: string;
    createdAt: string;
    updatedAt: string;
  }> {
    return apiFetch("/user", {
      method: "POST",
      json: data,
    });
  },

  // Obtener un usuario por ID
  getById(id: string): Promise<{
    id: string;
    name: string;
    lastName: string;
    email: string;
    active: boolean;
    lastLoginAt?: string;
    createdAt: string;
    updatedAt: string;
  }> {
    return apiFetch(`/user/${id}`);
  },

  // Actualizar un usuario
  update(
    id: string,
    data: {
      name?: string;
      lastName?: string;
      email?: string;
      active?: boolean;
      rut?: string;
      phone?: string;
      nationalityId?: string;
    },
  ): Promise<{
    id: string;
    name: string;
    lastName: string;
    email: string;
    active: boolean;
    lastLoginAt?: string;
    createdAt: string;
    updatedAt: string;
  }> {
    return apiFetch(`/user/${id}`, {
      method: "PUT",
      json: data,
    });
  },

  // Obtener roles efectivos de un usuario en sociedad activa
  getUserRoles(id: string): Promise<
    Array<{
      id: string;
      name: string;
      description: string;
      active: boolean;
    }>
  > {
    return apiFetch(`/user/${id}/roles`);
  },

  // Eliminar un usuario
  delete(id: string): Promise<{ message: string }> {
    return apiFetch(`/user/${id}`, {
      method: "DELETE",
    });
  },
};

export const orgPositionApi = {
  getByCompany(
    companyId: string,
    options?: { subscriptionId?: string; companyId?: string | null },
  ): Promise<
    Array<{
      id: string;
      parentPositionId?: string | null;
      code: string;
      name: string;
      description?: string | null;
      hierarchyPath?: string;
      hierarchyLevel?: number;
      sortOrder?: number;
      isDefault: boolean;
      active: boolean;
      createdAt?: string;
      updatedAt?: string;
    }>
  > {
    return apiFetch(`/companies/${companyId}/positions`, {
      subscriptionId: options?.subscriptionId ?? null,
      companyId: options?.companyId ?? null,
    });
  },

  getTree(companyId: string): Promise<
    Array<{
      id: string;
      parentPositionId?: string | null;
      code: string;
      name: string;
      description?: string | null;
      hierarchyPath: string;
      hierarchyLevel: number;
      sortOrder: number;
      isDefault: boolean;
      active: boolean;
      assignedUsers?: Array<{
        id: string;
        fullName: string;
        email: string;
        isPrimary: boolean;
      }>;
      userCompanyPositions?: Array<{
        id: string;
        isPrimary: boolean;
        user: {
          id: string;
          name: string;
          lastName: string;
          email: string;
        };
      }>;
      _count?: {
        userCompanyPositions?: number;
        roleAssignments?: number;
      };
      children?: unknown[];
    }>
  > {
    return apiFetch(`/companies/${companyId}/positions/tree`);
  },

  create(
    companyId: string,
    data: {
      code: string;
      name: string;
      description?: string;
      isDefault?: boolean;
      parentPositionId?: string;
      sortOrder?: number;
    },
  ) {
    return apiFetch(`/companies/${companyId}/positions`, {
      method: "POST",
      json: data,
    });
  },

  update(
    companyId: string,
    positionId: string,
    data: {
      code?: string;
      name?: string;
      description?: string;
      isDefault?: boolean;
      active?: boolean;
      parentPositionId?: string | null;
      sortOrder?: number;
    },
  ) {
    return apiFetch(`/companies/${companyId}/positions/${positionId}`, {
      method: "PATCH",
      json: data,
    });
  },

  getUserPositions(
    companyId: string,
    userId: string,
  ): Promise<
    Array<{
      id: string;
      isPrimary: boolean;
      active: boolean;
      position: {
        id: string;
        code: string;
        name: string;
        isDefault: boolean;
      };
    }>
  > {
    return apiFetch(`/companies/${companyId}/users/${userId}/positions`);
  },

  assignUserPosition(
    companyId: string,
    userId: string,
    data: { positionId: string; isPrimary?: boolean },
  ) {
    return apiFetch(`/companies/${companyId}/users/${userId}/positions`, {
      method: "POST",
      json: data,
    });
  },

  removeUserPosition(companyId: string, userId: string, positionId: string) {
    return apiFetch(
      `/companies/${companyId}/users/${userId}/positions/${positionId}`,
      {
        method: "DELETE",
      },
    );
  },

  getPositionUsers(
    companyId: string,
    positionId: string,
  ): Promise<
    Array<{
      id: string;
      userId: string;
      isPrimary: boolean;
      active: boolean;
      user: {
        id: string;
        name: string;
        lastName: string;
        email: string;
        active: boolean;
      };
    }>
  > {
    return apiFetch(`/companies/${companyId}/positions/${positionId}/users`);
  },

  getPositionRoles(
    companyId: string,
    positionId: string,
  ): Promise<
    Array<{
      id: string;
      roleId: string;
      active: boolean;
      role: {
        id: string;
        name: string;
        description: string;
        active: boolean;
      };
    }>
  > {
    return apiFetch(`/companies/${companyId}/positions/${positionId}/roles`);
  },

  setPositionRoles(companyId: string, positionId: string, roleIds: string[]) {
    return apiFetch(`/companies/${companyId}/positions/${positionId}/roles`, {
      method: "PUT",
      json: { roleIds },
    });
  },

  updateHierarchy(
    companyId: string,
    positionId: string,
    data: { parentPositionId?: string | null; sortOrder?: number },
  ) {
    return apiFetch(
      `/companies/${companyId}/positions/${positionId}/hierarchy`,
      {
        method: "PATCH",
        json: data,
      },
    );
  },

  delete(companyId: string, positionId: string) {
    return apiFetch(`/companies/${companyId}/positions/${positionId}`, {
      method: "DELETE",
    });
  },
};

export { apiFetch };
